import React, { useRef } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Tooltip,
  makeStyles,
  MenuItem,
  Menu,
  colors,
  Typography,
} from '@material-ui/core';
import {
  AttachFile,
  PlaylistAdd,
} from '@material-ui/icons';
import {
  Clipboard,
} from 'react-feather';

import { createEmailTemplate, updateEmailTemplate } from 'src/utils/api';

import BaseModal from 'src/components/Modal/BaseModal';
import TextField from 'src/components/TextField';
import useGetRequest from 'src/hooks/useGetRequest';
import ChipAutocomplete from 'src/components/ChipAutocomplete';
import { EmailTemplate, Step } from 'src/types';
import { useSnackbar } from 'notistack';
import SlateRichTextArea from '../../../../components/slate/SlateRichTextArea';

const TO = 'TO';
const BCC = 'BCC';
const CC = 'CC';

const useStyles = makeStyles((theme) => ({
  input: {
    width: '100%'
  },
  menu: {
    zIndex: 2001,
  },
  editor: {
    flexGrow: 1,
    minHeight: 300,
    padding: 0,
  },
  action: {
    marginRight: theme.spacing(1)
  },
  isTracked: {
    color: colors.blue['500'],
  },
}));

interface EmailTemplateBuilderModalProps {
  emailTemplateId: string,
  workflowId: string,
  onClose: () => void,
  onSuccess: () => void,
}

interface EmailTemplateBuilderModalContentProps {
  emailTemplate: EmailTemplate,
  workflowId: string,
  onClose: () => void,
  onSuccess: () => void,
}

function EmailTemplateBuilderModal({
  emailTemplateId,
  workflowId,
  onSuccess,
  onClose,
}: EmailTemplateBuilderModalProps) {
  const { result: emailTemplate, isLoading } = useGetRequest({
    // @ts-ignore - step will have an action
    url: `/automatic/workflows/${workflowId}/email-templates/${emailTemplateId}/`,
    initialResult: [],
  });

  if (isLoading) return null;

  return (
    <EmailTemplateBuilderModalContent
      emailTemplate={emailTemplate}
      workflowId={workflowId}
      onSuccess={onSuccess}
      onClose={onClose}
    />
  );
}

function EmailTemplateBuilderModalContent({
  emailTemplate,
  workflowId,
  onSuccess,
  onClose,
}: EmailTemplateBuilderModalContentProps) {
  const filterParticipants = (participantType: any) => (emailTemplate.participantRoles || []).filter((pr: any) => pr.type === participantType).map((pr: any) => pr.role.id);

  const classes = useStyles();
  const [isFieldsMenuOpen, setFieldsMenuOpen] = React.useState(false);
  const [isTemplatesMenuOpen, setTemplatesMenuOpen] = React.useState(false);

  const [tos, setTos] = React.useState(filterParticipants(TO));
  const [ccs, setCcs] = React.useState(filterParticipants(CC));
  const [bccs, setBccs] = React.useState(filterParticipants(BCC));
  const [subject, setSubject] = React.useState(emailTemplate.subject || '');
  const [body, setBody] = React.useState(emailTemplate.body || '');
  const [isTracked, setIsTracked] = React.useState(emailTemplate.isTracked || false);
  const [showCc, setShowCc] = React.useState(false);
  const [showBcc, setShowBcc] = React.useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const quillRef = useRef(null);
  const templatesAnchorEl = React.useRef(null);
  const anchorEl = React.useRef(null);

  const { result: emailTemplates } = useGetRequest({
    url: `/automatic/workflows/${workflowId}/email-templates/`,
    initialResult: [],
  });

  const { result: roles, isLoading: rolesLoading } = useGetRequest({
    url: `/automatic/workflows/${workflowId}/roles/`,
    initialResult: [],
  });

  if (rolesLoading) return null;

  const handleCreateTemplate = () => {
    const { id } = emailTemplate;
    const participantRoles = tos.map((to: any) => ({
      roleId: to,
      type: TO
    })).concat(ccs.map((cc: any) => ({
      roleId: cc,
      type: CC
    }))).concat(bccs.map((bcc: any) => ({
      roleId: bcc,
      type: BCC
    })));

    updateEmailTemplate(workflowId, id, {
      subject,
      body,
      isTracked,
      participantRoles,
    }).then(() => {
      onClose();
      onSuccess();
    }).catch(() => {
      enqueueSnackbar('Failed save email', {
        variant: 'error'
      });
    });
  };

  const fieldOptions = roles.reduce((acc: any, role: any) => acc.concat([
    {
      label: `${role.name} first name`,
      value: `roles-${role.name.toLowerCase()}-first_name`,
    },
    {
      label: `${role.name} last name`,
      value: `roles-${role.name.toLowerCase()}-last_name`,
    },
    {
      label: `${role.name} email`,
      value: `roles-${role.name.toLowerCase()}-email`,
    },
  ]), []);


  return (
    <BaseModal
      title="Edit template"
      maxWidth="md"
      onClose={onClose}
      isOpen
      boxProps={{ p: 0 }}
    >
      <Box pl={2} pr={2} pt={1} pb={1} display="flex" alignItems="center">
        <Box pr={1}>
          <Typography variant="h5">
            To:
          </Typography>
        </Box>
        <ChipAutocomplete
          options={roles.filter((role: any) => !tos.includes(role.id))}
          getOptionLabel={(role: any) => role && role.name}
          value={roles.filter((role: any) => tos.includes(role.id))}
          onChange={(event: any, _roles: any) => {
            setTos(_roles.map((_role: any) => _role.id));
          }}
          noBorder
        />
        <Box display="flex">
          <Button onClick={() => setShowCc(!showCc)}>
            Cc
          </Button>
        </Box>
        <Box>
          <Button onClick={() => setShowBcc(!showBcc)}>
            Bcc
          </Button>
        </Box>
      </Box>
      <Divider />
      {
        showCc && (
          <>
            <Box pl={2} pr={2} pt={1} pb={1} display="flex" alignItems="center">
              <Box pr={1}>
                <Typography variant="h5">
                  Cc:
                </Typography>
              </Box>
              <ChipAutocomplete
                options={roles.filter((role: any) => !ccs.includes(role.id))}
                getOptionLabel={(role: any) => role && role.name}
                value={roles.filter((role: any) => ccs.includes(role.id))}
                onChange={(event: any, _roles: any) => {
                  setCcs(_roles.map((_role: any) => _role.id));
                }}
                noBorder
              />
            </Box>
            <Divider />
          </>
        )
      }
      {
        showBcc && (
          <>
            <Box pl={2} pr={2} pt={1} pb={1} display="flex" alignItems="center">
              <Box pr={1}>
                <Typography variant="h5">
                  Bcc:
                </Typography>
              </Box>
              <ChipAutocomplete
                options={roles.filter((role: any) => !bccs.includes(role.id))}
                getOptionLabel={(role: any) => role && role.name}
                value={roles.filter((role: any) => bccs.includes(role.id))}
                onChange={(event: any, _roles: any) => {
                  setBccs(_roles.map((_role: any) => _role.id));
                }}
                noBorder
              />
            </Box>
            <Divider />
          </>
        )
      }
      <Divider />
      <Box p={2} display="flex" alignItems="center">
        <Box pr={1}>
          <Typography variant="h5">
            Subject:
          </Typography>
        </Box>
        <TextField
          className={classes.input}
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
          placeholder="Subject"
          value={subject}
          onChange={(e: any) => setSubject(e.target.value)}
        />
      </Box>
      <Divider />
      <SlateRichTextArea
        editorClassName={classes.editor}
        value={body}
        onChange={(value: any) => setBody(value)}
        workflowId={workflowId}
        showToolbar
      />
      <Divider />
      <Box
        display="flex"
        alignItems="center"
        py={1}
        px={2}
      >
        <Button
          color="secondary"
          variant="contained"
          className={classes.action}
          onClick={() => handleCreateTemplate()}
        >
          Save
        </Button>
        <Tooltip title="Attach file">
          <IconButton
            size="small"
            className={classes.action}
          >
            <AttachFile />
          </IconButton>
        </Tooltip>
        <Tooltip title="Add field">
          <IconButton
            size="small"
            className={classes.action}
            ref={anchorEl}
            onClick={() => setFieldsMenuOpen(true)}
          >
            <PlaylistAdd />
          </IconButton>
        </Tooltip>
        <Menu
          className={classes.menu}
          anchorEl={anchorEl.current}
          open={isFieldsMenuOpen}
          PaperProps={{ style: { width: 250 } }}
          onClose={() => setFieldsMenuOpen(false)}
        >
          {
            fieldOptions.map(({
              label,
              value
            }: any) => (
              <MenuItem
                key={value}
                onClick={() => {
                  if (quillRef && quillRef.current) {
                    // @ts-ignore
                    const range = quillRef.current.getEditor().getSelection();
                    // @ts-ignore
                    quillRef.current.getEditor().insertEmbed(range ? range.index : 0, `{{${value}}}`);
                    setFieldsMenuOpen(false);
                  }
                }}
              >
                {label}
              </MenuItem>
            ))
          }
        </Menu>
        <Tooltip title="Choose template">
          <IconButton
            size="small"
            className={classes.action}
            ref={templatesAnchorEl}
            onClick={() => setTemplatesMenuOpen(true)}
          >
            <Clipboard />
          </IconButton>
        </Tooltip>
        <Menu
          className={classes.menu}
          anchorEl={templatesAnchorEl.current}
          open={isTemplatesMenuOpen}
          PaperProps={{ style: { width: 250 } }}
          onClose={() => setTemplatesMenuOpen(false)}
        >
          {
            emailTemplates.map((template: any) => (
              <MenuItem
                key={template.id}
                onClick={() => {
                  setTos(template.tos || []);
                  setSubject(template.subject);
                  setBody(template.body);
                  setIsTracked(template.isTracked);
                  setTemplatesMenuOpen(false);
                }}
              >
                {template.subject}
              </MenuItem>
            ))
          }
        </Menu>
      </Box>
    </BaseModal>
  );
}

export default EmailTemplateBuilderModal;
